<template>
  <button
    id="back-to-top"
    aria-label="Back to Top"
    :class="[
      $style.back,
      { [$style.active]: scrolled },
      { [$style.dark]: overFooter },
      { [$style.audioPlayerOpen]: tracks.length },
    ]"
    :ga4-event="
      JSON.stringify({
        click_type: 'back_link',
        component: 'BackToTop',
        content_group: `${parent}`,
        gtm_tag: 'interaction',
        link_text: 'Top',
      })
    "
    tabindex="1"
    type="button"
    @click.prevent="toTop"
  >
    <IconChevron></IconChevron>
    Top
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import IconChevron from '@/assets/svgs/IconChevron.svg?component';
import { useA11y } from '@/composables/Common.js';
import { useAudioStore } from '@/stores/audioStore';
import { useMainStore } from '@/stores/mainStore';
import { useRoute } from 'vue-router';

const audioStore = useAudioStore();
const { focusSkip } = useA11y();
const route = useRoute();
const store = useMainStore();
const { tracks } = storeToRefs(audioStore);

const overFooter = ref(false);
const scrolled = ref(false);

const parent = computed(() => {
  return route.matched[route.matched.length - 1].name;
});

onMounted(() => {
  nextTick(() => {
    document.addEventListener('scroll', onScroll);
  });
});

onBeforeUnmount(() => {
  document.removeEventListener('scroll', onScroll);
});

function handleIntersection(entries) {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      overFooter.value = true;
    }

    if (!entry.isIntersecting) {
      overFooter.value = false;
    }
  });
}

function onScroll() {
  const clearance = document.body.scrollTop > 20 || document.documentElement.scrollTop > 20;

  if (clearance) {
    // 20px clearance at top until trigger of "Top" button
    scrolled.value = true;
  }

  if (!clearance) {
    scrolled.value = false;
  }

  const footer = document.querySelector('#gugg-footer');
  const observer = new IntersectionObserver(handleIntersection);

  if (footer) {
    observer.observe(footer);
  }
}

function toTop() {
  document.documentElement.scrollTop = 0;

  if (store.isKeyboardUser) {
    focusSkip();
  }
}
</script>

<style lang="scss" module>
[type=button].back {
  @include info;

  & {
    background-color: $black;
    border-radius: 50%;
    bottom: 1rem;
    color: $white;
    cursor: pointer;
    display: none;
    height: 50px;
    line-height: 1rem;
    margin: 0;
    padding: px-to-rem(6.25);
    position: fixed;
    right: 1rem;
    width: 50px;
    z-index: get-zindex(back);
  }

  svg {
    display: block;
    margin: 0 auto;
  }

  &.active {
    display: block;
  }

  &.audio-player-open {
    bottom: 4rem;
  }

  &:hover {
    text-decoration: underline;
  }

  &:active,
  &:focus {
    outline-color: $black;

    svg {
      path {
        color: $white;
      }
    }
  }
}
</style>
